import { type FC, useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { formatPhoneNumber } from 'react-phone-number-input'
import { CheckCircleIcon } from '@heroicons/react/20/solid'
import {
  useAccountRecovery,
  AccountVerificationType,
  AccountRecoveryStatus,
  AccountRecoveryErrorType,
  useAccountRecoveryV2,
} from '@egw/api'
import { Button, Input, Alert, AlertType } from '@egw/components'
import { type FeatureFlags, WEALTH_ROUTES } from '@egw/constants'
import { useTranslation } from '@egw/i18n'
import { useRouter } from '@egw/router'
import { Logger } from '@egw/logger'
import { sendEvent } from '@egw/events'
import { useFlags } from '@egw/feature-flags'
import { Layout, PageHeader } from '../../../components'
import { getRecoveryFlowData, updateRecoveryFlowData } from '../../../services'
import { type RecoveryFlow } from '../../../models'

const log = new Logger('Passkey Recovery - Submit OTP')

interface FormData {
  otp: string
}

export const RecoveryPhoneOtpPage: FC = () => {
  const [flowData, setFlowData] = useState<RecoveryFlow | null>(null)
  const [isOtpResent, setIsOtpResent] = useState(false)

  const { flags } = useFlags<FeatureFlags>()

  const { t } = useTranslation('features-auth')
  const { navigate } = useRouter()

  const v2Result = useAccountRecoveryV2()
  const v1Result = useAccountRecovery()
  const {
    mutate,
    isLoading,
    error,
    isSuccess,
    data: responseData,
  } = flags.rolloutIntegratedDocumentVerification ? v2Result : v1Result

  const {
    control,
    handleSubmit,
    formState: { errors: formErrors, isValid },
    setValue,
    setError,
  } = useForm({
    mode: 'all',
    defaultValues: {
      otp: '',
    },
  })

  useEffect(() => {
    const d = getRecoveryFlowData()
    if (d?.phone) {
      setFlowData(d)
    } else {
      navigate(WEALTH_ROUTES.RECOVERY.path)
    }
  }, [navigate])

  // Hide resent message after 5s
  useEffect(() => {
    if (isOtpResent) {
      setTimeout(() => {
        setIsOtpResent(false)
      }, 5000)
    }
  }, [isOtpResent])

  const onSubmit = handleSubmit(async (fData: unknown): Promise<void> => {
    const formData = fData as FormData
    try {
      const response = await mutate({
        verifyType: AccountVerificationType.PHONE_OTP,
        verificationToken: formData.otp,
      })
      if (
        response?.__typename ===
        (flags.rolloutIntegratedDocumentVerification ? 'AccountRecoveryResponseV2' : 'AccountRecoveryResponse')
      ) {
        const { status } = response
        log.debug('Submit OTP success: ', status)
        sendEvent('eg_verification_success', {
          eventSource: 'egWealth',
          category: 'passkey-recovery',
          subCategory: 'verification',
          verificationType: 'phone',
        })
        if (status === AccountRecoveryStatus.PENDING_DOCV_RESULT) {
          if (flags.rolloutIntegratedDocumentVerification) {
            updateRecoveryFlowData((draft) => {
              if ('documentVerificationBaseUrl' in response) {
                draft.documentVerificationBaseUrl = response.documentVerificationBaseUrl
              }
              draft.isPhoneVerified = true
              if ('verificationToken' in response) {
                draft.verificationToken = response.verificationToken as string
              }
              if ('verificationId' in response) {
                draft.verificationId = response.verificationId
              }
              if ('documentVerificationUrl' in response) {
                draft.documentVerificationUrl = response.documentVerificationUrl
              }
            })
          } else {
            updateRecoveryFlowData((draft) => {
              if ('documentVerificationUrl' in response) {
                draft.documentVerificationUrl = response.documentVerificationUrl
              }
              draft.isPhoneVerified = true
            })
          }
        } else {
          setError('otp', {
            type: 'manual',
            message: t('recovery.generic_error'),
          })
        }
      }
      if (response?.__typename === 'AccountRecoveryError') {
        if (response.errorType === AccountRecoveryErrorType.EXPIRED) {
          navigate(`${WEALTH_ROUTES.SIGN_IN.path}?recoveryExpired=true`)
          return
        }
        if (response.errorType === AccountRecoveryErrorType.UNABLE_TO_VALIDATE_VERIFICATION_METHOD) {
          setError('otp', {
            type: 'manual',
            message: t('recovery.invalid_otp_code'),
          })
        }
      }

      navigate(WEALTH_ROUTES.RECOVERY_SUBMIT.path)
    } catch (e) {
      log.error('Error sending phone recovery code', e)
    }
  })

  return (
    <Layout>
      {!(
        isSuccess &&
        responseData?.__typename ===
          (flags.rolloutIntegratedDocumentVerification ? 'AccountRecoveryResponseV2' : 'AccountRecoveryResponse') &&
        responseData.status === AccountRecoveryStatus.PENDING_DOCV_RESULT
      ) ? (
        <form onSubmit={(e) => void onSubmit(e)} className="flex-1 flex flex-col justify-start md:justify-center">
          <PageHeader title={t('recovery.title')} backLinkUrl={WEALTH_ROUTES.RECOVERY_SUBMIT.backPath} />

          <p className="mb-sm">{t('recovery.enter_code')}</p>

          <Controller
            name="otp"
            control={control}
            rules={{
              required: t('recovery.invalid_otp'),
              validate: (value) => value.length === 6 || t('recovery.invalid_otp'),
            }}
            render={({ field }) => (
              <Input
                {...field}
                label={t('recovery.6_digit_code')}
                className="max-w-[610px]"
                valueFormatter={(v) => v.replace(/[^0-9]/g, '')}
                required
                error={formErrors.otp?.message}
                maxLength={6}
                disabled={isLoading}
                onChange={(e) => {
                  field.onChange(e)
                  if (formErrors.otp) {
                    setValue('otp', e.target.value, { shouldValidate: true })
                  }
                }}
              />
            )}
          />

          <div className="align-center my-6 flex flex-row">
            <button
              id="magic-link-resend_link_btn"
              data-testid="magic-link-resend_link_btn"
              type="button"
              disabled={isLoading || isOtpResent}
              className="flex items-center justify-center text-btnLinkText hover:text-btnLinkTextHover active:text-btnLinkTextActive disabled:text-btnLinkTextDisabled hover:underline font-semibold border-transparent mr-4 text-sm md:w-fit h-10 cursor-pointer"
              onClick={() => {
                sendEvent('user_reSendVerification_click', {
                  eventSource: 'egWealth',
                  category: 'passkey-recovery',
                  subCategory: 'verification',
                  verificationType: 'phone',
                })
                void mutate({
                  phone: flowData?.phone,
                  sendVerificationMethod: true,
                  verifyType: AccountVerificationType.PHONE_OTP,
                })
              }}
            >
              {t('recovery.resend_code')}
            </button>

            {isOtpResent ? (
              <div className="flex items-center">
                <CheckCircleIcon className="mr-2 h-4 w-4 fill-green-400" />
                <span className="font-semibold text-sm text-green-600">{t('recovery.code_sent')}</span>
              </div>
            ) : null}
            {isOtpResent && error ? <p className="text-error text-sm">{t('recovery.otp_code_send_error')}</p> : null}
          </div>

          <div className="flex-1 md:hidden" />

          {error ? <Alert alertType={AlertType.Error} description={t('recovery.generic_error')} className="mt-sm" /> : null}

          <Button
            type="submit"
            isDisabled={!isValid}
            isLoading={isLoading}
            testId="submitVerifyOtp_primary_btn"
            className="w-full md:w-min mt-sm"
          >
            {t('recovery.continue')}
          </Button>
        </form>
      ) : null}

      {isSuccess &&
      responseData?.__typename ===
        (flags.rolloutIntegratedDocumentVerification ? 'AccountRecoveryResponseV2' : 'AccountRecoveryResponse') &&
      responseData.status === AccountRecoveryStatus.PENDING_DOCV_RESULT ? (
        <div className="flex-1 flex flex-col justify-start md:justify-center">
          <PageHeader
            title={t('recovery.mobile_phone_verified')}
            backLinkUrl={WEALTH_ROUTES.RECOVERY_SUBMIT.backPath}
          />
          <div className="flex items-center pt-lg md:pt-0">
            <CheckCircleIcon className="mr-2 h-4 w-4 fill-green-400" />
            <span className="text-green-600">
              {t('recovery.mobile_number_verified', {
                mobile: formatPhoneNumber(
                  `+${flowData?.phone?.countryPhoneCode.toString() ?? ''}${flowData?.phone?.phoneNumber ?? ''}`,
                ),
              })}
            </span>
          </div>

          <p className="mt-4 md:mb-24">{t('recovery.mobile_number_verification_complete')}</p>

          <div className="flex-1 md:hidden" />
          <Button
            id="phoneVerifiedContinueButton"
            className="w-full md:w-max"
            onClick={() => {
              navigate(WEALTH_ROUTES.RECOVERY_VERIFICATION.path)
            }}
          >
            {t('recovery.continue')}
          </Button>
        </div>
      ) : null}
    </Layout>
  )
}
